import { observable, action, computed, reaction } from 'mobx';
import i18next from 'i18next';
import Alert from 'react-s-alert';

import { AppManager, Input } from 'core';
import { goBack } from 'core/routing';
import { analytics } from 'core/analytics';

import { WizardStore } from 'libs/wizards/app';
import { clientStore } from 'apps/client/app';
import { TagsApp } from 'apps/tag/app';
import { SpaceApp, SpacesApp } from 'apps/space/app';
import { ProfilesApp } from 'apps/profile/app';
import { ProjectMutation } from 'apps/project/gql';
import { redirectToContent, raiseUserError } from 'apps/utils';
import { conf } from 'apps/project/config.yaml';
import { modalStore } from 'apps/modals/app';

import { isTrialExpired } from 'utils/freeTrials';

// import { FreeTrialMessage } from 'utils/freeTrialMessage';

import { PaymentSpaceApp } from 'apps/payment/app';
import { SubscriptionAppManager } from 'apps/payment/subscription/appManager';

export class NewProjectAppManager extends AppManager {
  static registryName = 'NewProjectAppManager';
  @observable projectType = 'private';
  @observable name = '';
  @observable description = '';
  @observable phase = null;
  @observable license;
  @observable paymentMethod = null;
  @observable hasSubscribed = false;
  @observable emailInvites = [];
  @observable countryCode = '';
  @observable taxId = '';

  constructor(parent, init = {}, options) {
    super(parent, init, options);
    this.AppRef('inSpace', SpaceApp);
    this.AppRef('invites', ProfilesApp);
    this.AppRef('tags', TagsApp);
    this.AppRef('optionsOverride', SpacesApp);
    this.invites = new ProfilesApp(this);
    this.tags = new TagsApp(this);

    // mutates conf
    // this.patchTrialMessage(conf);

    this.wizard = new WizardStore(this, conf, 'projectWizard', {
      CreateProject: this.CreateProject,
    });

    this.newProjectInput = new Input({
      private: () => !!this.projectType && this.projectType === 'private',
      spaceId: () => this.inSpace && this.inSpace.id,
      name: () => this.name,
      description: () => this.description,
      phaseId: () => {
        if (this.phase) {
          return this.phase.id;
        }
        return null;
      },
      tags: () =>
        this.tags.map(({ id, name, deleted }) => ({ id, name, deleted })),
      invites: () => this.invites.map(({ username }) => username),
    });

    this.disposers.push(
      reaction(() => this.newProjectInput.invites, this.patchBillingWarning),
    );
    this.disposers.push(
      reaction(
        () => ({
          spaceId: this.newProjectInput.spaceId,
          projectType: this.projectType,
          hasProSubscription: this.inSpace?.hasProSubscription,
        }),
        () => {
          const form = this.wizard.formStores.get('info');
          form.SetButtonDisabled(this.requiresPayment);
        },
      ),
    );

    this.disposers.push(
      reaction(
        () => ({
          spaceType: this.inSpace?.spaceType,
          hasProSubscription: this.inSpace?.hasProSubscription,
        }),
        ({ spaceType, hasProSubscription }) => {
          const projectTypeFieldStore = this.wizard.formStores
            .get('info')
            .fieldStores.get('projectType');
          const shouldHideProjectTypeField =
            spaceType === 'INITIATIVE' && hasProSubscription;
          projectTypeFieldStore.hide = shouldHideProjectTypeField;
          if (shouldHideProjectTypeField) {
            this.projectType = 'private';
          }
        },
      ),
    );

    this.disposers.push(
      reaction(
        () => ({
          workspaces: clientStore.workspaces,
          activeSpaceId: clientStore.activeSpaceId,
          optionsManager: this.wizard.formStores
            .get('info')
            .fieldStores.get('inSpace').optionsManager,
        }),
        ({ workspaces, activeSpaceId, optionsManager }) => {
          if (!optionsManager) return;
          this.optionsOverride = workspaces;
          // eslint-disable-next-line no-param-reassign
          optionsManager.options = this.optionsOverride;

          const activeSpace = optionsManager.options.find(
            option => option.id === activeSpaceId,
          );

          if (activeSpace) this.inSpace = activeSpace;
        },
      ),
    );
  }

  @action patchBillingWarning = () => {
    const infoForm = this.wizard.formStores.get('info');
    const invitesField = infoForm.fieldStores.get('invites');
    if (this.newProjectInput.private && invitesField.value.length > 0) {
      infoForm.warning =
        'Note: inviting collaborators to private projects may incur additional charges.';
    } else {
      infoForm.warning = null;
    }
  };
  /*
  @action patchTrialMessage = formConf => {
    const privateFieldConf =
      formConf?.fields?.projectType?.options?.values?.[0];
    if (!privateFieldConf) return;
     privateFieldConf.extraMessage = {
      component: FreeTrialMessage,
      props: { appManager: this },
      showOnSelect: true,
    }; 
  }; */

  @computed get requiresPayment() {
    if (
      !this.inSpace ||
      this.hasSubscribed ||
      this.inSpace.spaceType === 'PROFILE'
    ) {
      return false;
    }

    const { hasProSubscription } = this.inSpace;

    return !hasProSubscription;
  }

  @action Cancel = () => {
    goBack();
  };

  @action onSysErr = () => {
    this.wizard.activeForm.SetPosting(false);
    clientStore.SetPosting(false);
    Alert.error(
      i18next.t('Hmm... We failed to create your Project. Please try again.'),
    );
  };

  @action onUserErr = error => {
    this.wizard.activeForm.SetPosting(false);
    clientStore.SetPosting(false);
    raiseUserError(error, 'create', 'Project');
  };

  @action onSuccess = result => {
    const { hasBillingPermissions, hasProSubscription, trialPeriodEnd } =
      this.inSpace;
    if (
      this.newProjectInput.private &&
      hasBillingPermissions &&
      !hasProSubscription &&
      !trialPeriodEnd
    ) {
      analytics.sendEvent(
        'start-free-trial',
        { space: this.inSpace.path },
        // HubSpot?
        { ga: true },
      );
      analytics.sendEvent(
        'StartTrial',
        { value: 7, currency: 'EUR' },
        { twitter: true, facebook: true },
      );
    }

    clientStore.SetActiveWorkspaceIfValid(this.inSpace.id);
    clientStore.UpdateProfileOrTeamWorkspace();

    analytics.sendEvent(
      'Schedule',
      { currency: 'EUR', value: 8 },
      {
        facebook: true,
      },
    );
    analytics.sendEvent('created-new-project');
    this.wizard.activeForm.SetPosting(false);
    this.wizard.clearDraft();
    clientStore.SetPosting(false);
    redirectToContent(result, 'project.files');
    Alert.success(i18next.t('Your Project has been created successfully!'));
  };

  @action CreateProject = () => {
    if (this.wizard.activeForm.isValid) {
      this.wizard.activeForm.SetPosting(true);
      clientStore.SetPosting(true);
      const { onSysErr, onUserErr, onSuccess } = this;
      const options = { onSysErr, onUserErr, onSuccess };
      this.mutations.push(ProjectMutation.New(this.newProjectInput, options));
    } else {
      this.wizard.activeForm.alert();
    }
  };

  subscribe = () => {
    analytics.sendEvent('clicked-upgrade-button-new-project');
    const parameters = {
      params: this.params,
      spaceSlug: this.inSpace.path,
    };
    const subscriptionAppManager = new SubscriptionAppManager(this, parameters);
    const paymentSpace = new PaymentSpaceApp(
      subscriptionAppManager,
      parameters,
    );
    if (!paymentSpace || (paymentSpace && !paymentSpace.preview)) {
      subscriptionAppManager.NewSubscriptionQuery({
        preview: true,
      });
    }
    paymentSpace.ConfirmSubscribe({
      onError: error => {
        this.wizard.activeForm.SetPosting(false);
        clientStore.SetPosting(false);
        this.wizard.activeForm.manager.OnSubscribeError(error);
      },
      onSuccess: () => {
        this.hasSubscribed = true;
      },
    });
  };
}
